/**
 * @typedef {import('./BlogFilterSearchProps').default} Props
 * @typedef {import('./BlogFilterSearchState').default} State
 * @typedef {{tagId: string; slug: string; tagName: string}} CategoryFilter
 * @typedef {{tagId: string; tagName: string}} TagFilter
 * @typedef {{
 * 	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
 * 	submitForm: () => void;
 * }} SearchFormActions
 * @typedef {{ target: { value: string } }} InputChangeEvent
 */

import { translate } from '../App/Internationalization';
import * as yup from 'yup';
import BaseField from '../shared/Forms/Fields/BaseField';
import BaseForm from '../shared/Forms/BaseForm';
import BlogCategoryFilterSlug from '../config/local/blogCategoryFilter';
import BlogFilter from './BlogFilter';
import Loading from '../shared/Loading';
import React from 'react';
import SubmitButton from '../shared/Forms/Inputs/SubmitButton';

const validationSchema = yup.object().shape({
	searchQuery: yup.string(),
});
const categoryFirstRowOrder = BlogCategoryFilterSlug.slice(0, 4);
const categorySecondRowOrder = BlogCategoryFilterSlug.slice(4, 8);

/**
 * @param {readonly string[]} slugOrder
 * @param {readonly CategoryFilter[]} categories
 * @param {Set<string>} usedTagIds
 * @returns {CategoryFilter[]}
 */
function resolveCategoryRow(slugOrder, categories, usedTagIds) {
	/** @type {CategoryFilter[]} */
	const rowCategories = [];

	for (const slugValue of slugOrder) {
		const category = categories.find((tag) => tag.slug === slugValue);

		if (category === undefined) {
			continue;
		}

		usedTagIds.add(category.tagId);
		rowCategories.push(category);
	}

	return rowCategories;
}

/**
 * @param {readonly CategoryFilter[]} categories
 * @returns {{
 * 	firstRowCategories: CategoryFilter[];
 * 	secondRowCategories: CategoryFilter[];
 * 	remainingCategories: readonly CategoryFilter[];
 * }}
 */
function getOrderedCategoryRows(categories) {
	const usedTagIds = new Set();
	const firstRowCategories = resolveCategoryRow(
		categoryFirstRowOrder,
		categories,
		usedTagIds
	);
	const secondRowCategories = resolveCategoryRow(
		categorySecondRowOrder,
		categories,
		usedTagIds
	);
	const remainingCategories = categories.filter(
		(category) => !usedTagIds.has(category.tagId)
	);

	return {
		firstRowCategories,
		remainingCategories,
		secondRowCategories,
	};
}

/** @extends {React.Component<Props, State>} */
export default class BlogFilterSearch extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);

		this.state = {
			allPostTags: this.props.tagFilterList || [],
			selectedCategory: this.props.selectedCategoryId ?? null,
			selectedTag: this.props.selectedTagId ?? null,
		};

		this.handleSubmit = this.handleSubmit.bind(this);
	}

	/**
	 * @param {Props} prevProps
	 */
	componentDidUpdate(prevProps) {
		this.syncTagFilterList(prevProps);
		this.syncSelectedFilters(prevProps);
	}

	/**
	 * @param {Props} prevProps
	 */
	syncTagFilterList(prevProps) {
		if (prevProps.tagFilterList !== this.props.tagFilterList) {
			this.setState({
				allPostTags: this.props.tagFilterList || [],
			});
		}
	}

	/**
	 * @param {Props} prevProps
	 */
	syncSelectedFilters(prevProps) {
		if (
			prevProps.selectedCategoryId !== this.props.selectedCategoryId ||
			prevProps.selectedTagId !== this.props.selectedTagId
		) {
			const selectedCategory = this.props.selectedCategoryId ?? null;
			const selectedTag =
				this.props.selectedTagId === selectedCategory
					? null
					: this.props.selectedTagId ?? null;

			this.setState({
				selectedCategory,
				selectedTag,
			});
		}
	}

	/**
	 * @param {string} categoryId
	 */
	handleCategoryFilter(categoryId) {
		this.setState(
			(/** @type {{ selectedCategory: string | null; }} */ prev) => ({
				selectedCategory:
					prev.selectedCategory === categoryId ? null : categoryId,
				selectedTag: null,
			}),
			() => this.triggerFilterChange()
		);
	}

	/**
	 * @param {string} tagId
	 */
	handleTagFilter(tagId) {
		this.setState(
			(/** @type {{ selectedTag: string | null; }} */ prev) => ({
				selectedTag: prev.selectedTag === tagId ? null : tagId,
			}),
			() => this.triggerFilterChange()
		);
	}

	/**
	 * @param {{searchQuery: string}} values
	 * @param {(isSubmitting: boolean) => void} [setSubmitting]
	 */
	async handleSubmit(values, setSubmitting) {
		const hasSearchQuery = values.searchQuery.trim() !== '';
		const submitValues = {
			...values,
			categoryId: hasSearchQuery ? null : this.state.selectedCategory,
			tagId: hasSearchQuery ? null : this.state.selectedTag,
		};

		if (hasSearchQuery) {
			this.setState({
				selectedCategory: null,
				selectedTag: null,
			});
		}

		try {
			await this.props.onSubmit(submitValues);
		} finally {
			setSubmitting?.(false);
		}
	}

	triggerFilterChange() {
		this.props.onSubmit({
			categoryId: this.state.selectedCategory ?? null,
			searchQuery: this.props.searchQuery ?? '',
			tagId: this.state.selectedTag ?? null,
		});
	}

	/**
	 * @param {JSX.Element[]} additionalFilters
	 * @returns {JSX.Element | JSX.Element[]}
	 */
	renderTagFilters(additionalFilters) {
		if (this.state.allPostTags.length > 0) {
			return additionalFilters;
		}

		return <Loading />;
	}

	/**
	 * @param {JSX.Element[]} additionalFilters
	 * @returns {JSX.Element | null}
	 */
	renderTagSection(additionalFilters) {
		if (this.state.selectedCategory === null) {
			return null;
		}

		return (
			<section className="input-group checkbox-group mt-5">
				<h4 className="hl-ms-1">{this.props.tagTitle ?? 'Tags'}</h4>
				<ul className="button-group my-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
					{this.renderTagFilters(additionalFilters)}
				</ul>
			</section>
		);
	}

	/**
	 * @param {readonly CategoryFilter[]} categories
	 * @returns {JSX.Element[]}
	 */
	renderCategoryFilters(categories) {
		return categories.map((category) => (
			<BlogFilter
				disabled={false}
				id={`category-filter-${category.tagId}`}
				key={category.tagId}
				label={category.tagName}
				name={'categoryFilter'}
				onChange={() => this.handleCategoryFilter(category.tagId)}
				selected={this.state.selectedCategory === category.tagId}
			/>
		));
	}

	render() {
		const { firstRowCategories, remainingCategories, secondRowCategories } =
			getOrderedCategoryRows(this.props.categoryFilterList);

		const additionalFilters = [...this.state.allPostTags]
			.filter((tag) => tag.tagId !== this.state.selectedCategory)
			.sort((firstTag, secondTag) =>
				firstTag.tagName.localeCompare(secondTag.tagName, undefined, {
					sensitivity: 'base',
				})
			)
			.map((/** @type {TagFilter} */ tag) => (
				<BlogFilter
					disabled={false}
					id={`tag-filter-${tag.tagId}`}
					key={tag.tagId}
					label={tag.tagName}
					name={'additionalFilter'}
					onChange={() => this.handleTagFilter(tag.tagId)}
					selected={this.state.selectedTag === tag.tagId}
				/>
			));

		return (
			<section className={this.props.className ?? 'w-3/4'}>
				<BaseForm
					className="flex w-full items-end"
					enableReinitialize={true}
					initialValues={{
						searchQuery: this.props.searchQuery ?? '',
					}}
					noRequiredText={true}
					onSubmit={this.handleSubmit}
					validationSchema={validationSchema}
				>
					<BaseField
						className="flex-grow mr-2"
						disabled={this.props.disabled}
						inputClass="h-11"
						labelClass="input-label"
						name="searchQuery"
						onChange={(
							/** @type {InputChangeEvent} */ event,
							/** @type {SearchFormActions} */ form
						) => {
							form.setFieldValue(
								'searchQuery',
								event.target.value
							);
							if (event.target.value === '') {
								form.submitForm();
							}
						}}
						placeholder={translate(
							'global.blog.forms.search.placeholder'
						)}
						type="search"
					/>

					<SubmitButton disabled={this.props.disabled}>
						{translate('global.forms.buttons.search')}
					</SubmitButton>
				</BaseForm>
				<section className="input-group checkbox-group mt-5">
					<h4 className="hl-ms-1">
						{this.props.categoryTitle ?? 'Categories'}
					</h4>
					<ul className="button-group my-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
						{this.renderCategoryFilters(firstRowCategories)}
					</ul>
					<ul className="button-group mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
						{this.renderCategoryFilters(secondRowCategories)}
					</ul>
					{remainingCategories.length > 0 && (
						<ul className="button-group mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
							{this.renderCategoryFilters(remainingCategories)}
						</ul>
					)}
				</section>
				{this.renderTagSection(additionalFilters)}
			</section>
		);
	}
}
